import { useState, useEffect } from "react";
import axios from "axios";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import CreatableSelect from "react-select/creatable";
import { toast, ToastContainer } from "react-toastify";
import { useAuth } from "../../utils/AuthContext";
import PageHeading from "../PageHeading";
import DynamicSelect from "../DynamicSelect";
import useDoctors from "../../hook/useDoctors";


const getAvailableDatesForMonth = (selectedMonth, availableDays) => {
  const year = selectedMonth.getFullYear();
  const month = selectedMonth.getMonth();
  const totalDays = new Date(year, month + 1, 0).getDate();
  const availableDates = [];
  for (let day = 1; day <= totalDays; day++) {
    const date = new Date(year, month, day);
    if (availableDays.includes(date.getDay())) {
      availableDates.push(date);
    }
  }
  return availableDates;
};

const API_KEY = "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";
const BASE_URL = "https://api.muktihospital.com/api";

const fetchData = async (endpoint) => {
  try {
    const response = await axios.get(`${BASE_URL}${endpoint}`, {
      headers: { "x-api-key": API_KEY },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching data:", error);
    return null;
  }
};

const BookAppointment = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [patientLoading, setPatientLoading] = useState(true);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [appointmentData, setAppointmentData] = useState([]);
  const { doctors, loading: doctorLoading, fetchDoctors } = useDoctors();
  const [patients, setPatients] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [availableDates, setAvailableDates] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState({
    value: "CASH",
    label: "Cash"
  });
  const [selectedFee, setSelectedFee] = useState(null);
  const [feeOptions, setFeeOptions] = useState([]);
  const [selectedBloodGroup, setSelectedBloodGroup] = useState(null);
  const [loggedInDoctorDetails, setLoggedInDoctorDetails] = useState(null);

  // Form data state
  const [formData, setFormData] = useState({
    doctor: "",
    patient: "",
    consultationFee: "",
    paymentMethod: "CASH",
    reference: "",
    mobileNumber: "",
    weight: "",
    age: "",
    serialNumber: "",
    reason: "",
    address: "",
    bloodGroup: "",
    isManualPatient: false,
  });

  // Initial form state for resets
  const initialFormData = {
    doctor: "",
    patient: "",
    consultationFee: "",
    paymentMethod: "",
    reference: "",
    mobileNumber: "",
    weight: "",
    age: "",
    serialNumber: "",
    reason: "",
    address: "",
    bloodGroup: "",
    isManualPatient: false,
  };

 useEffect(() => {
        fetchDoctors();

        const fetchPatients = async () => {
             try {
                 const response = await axios.get(`${BASE_URL}/patient`, {
                     headers: { "x-api-key": API_KEY },
                 });
                 const patientData = Array.isArray(response.data) ? response.data : [];
                 const formattedPatients = patientData.map((patient) => ({
                     value: patient.id,
                     label: `${patient.name} (${patient.phoneNumber || "No Phone"})`,
                     phone: patient.phoneNumber || "",
                 }));
                 setPatients(formattedPatients);
             } catch (error) {
                 console.error("Error loading patients:", error);
                 setPatients([]);
             } finally {
                 setPatientLoading(false);
             }
         };
        fetchPatients();

        const loadAppointments = async () => {
             const appointmentsRes = await fetchData("/appointment");
             if (appointmentsRes && appointmentsRes.appointments) {
                 setAppointmentData(appointmentsRes.appointments);
             } else if (Array.isArray(appointmentsRes)) {
                 setAppointmentData(appointmentsRes);
             } else {
                 setAppointmentData([]);
             }
         };
        loadAppointments();

    }, [fetchDoctors]);

useEffect(() => {
        if (user?.email && doctors.length > 0) {
            setScheduleLoading(true);
            const profile = doctors.find(doc => doc.email === user.email);

            if (profile) {
                setLoggedInDoctorDetails(profile);
                const appointmentFee = profile.translations?.en?.appointmentFee;
                const followUpFee = profile.translations?.en?.followUpFee;
                const options = [];
                // Add fees only if they are valid numbers/strings that can be parsed
                if (appointmentFee && !isNaN(parseFloat(appointmentFee))) options.push({ value: appointmentFee, label: `Appointment Fee (${appointmentFee})` });
                if (followUpFee && !isNaN(parseFloat(followUpFee))) options.push({ value: followUpFee, label: `Follow-Up Fee (${followUpFee})` });
                setFeeOptions(options);
                setSelectedFee(null);

                const availableDaysNames = profile.schedule?.map(sch => sch.day) || [];
                setAvailableDates(getAvailableDatesForMonth(selectedDate, availableDaysNames));

            } else {
                console.error("Could not find a doctor profile matching the logged-in user's email:", user.email);
                toast.error("Could not load your doctor profile. Please contact support.");
            }
            setScheduleLoading(false);
        }
         else if (doctors.length > 0 && !user?.email) {
             console.log("Doctors loaded, waiting for user email...");
             setScheduleLoading(false);
         }

    }, [user, doctors, selectedDate]);

  const handleDateChange = (date) => {
        const localDate = new Date(date);
        localDate.setHours(12, 0, 0, 0);
        setSelectedDate(localDate);
    };

  const handleActiveStartDateChange = ({ activeStartDate }) => {
        if (!loggedInDoctorDetails) return;
        const availableDays = loggedInDoctorDetails.schedule?.map(sch => sch.day) || [];
        setAvailableDates(getAvailableDatesForMonth(activeStartDate, availableDays));
    };

  // Handle fee change
  const handleFeeChange = (selectedOption) => {
    if (selectedOption) {
      setSelectedFee({ value: selectedOption.value, label: selectedOption.value });
      setFormData({ ...formData, consultationFee: selectedOption.value });
    } else {
      setSelectedFee(null);
      setFormData({ ...formData, consultationFee: "" });
    }
  };

  // Handle patient selection
  const handlePatientChange = (selectedOption) => {
    setSelectedPatient(selectedOption);
    setFormData({
      ...formData,
      patient: selectedOption?.value || "",
      mobileNumber: selectedOption?.phone || "",
      isManualPatient: selectedOption?.value?.startsWith("new-") || false,
    });
  };

  // Handle new patient creation
  const handleCreatePatient = (inputValue) => {
    const trimmedInput = inputValue.trim();
    if (!trimmedInput) {
      toast.warn("Patient name cannot be empty!");
      return;
    }

    // Check for duplicate patient names (case-insensitive)
    const isDuplicate = patients.some(
      (patient) => patient.label.toLowerCase() === trimmedInput.toLowerCase()
    );
    if (isDuplicate) {
      toast.warn("Patient with this name already exists!");
      return;
    }

    // Create new patient
    const newPatient = {
      value: `new-${Date.now()}`,
      label: trimmedInput,
      phone: "",
    };

    // Update patients list and select the new patient
    setPatients((prev) => [...prev, newPatient]);
    setSelectedPatient(newPatient);
    setFormData({
      ...formData,
      patient: newPatient.value,
      mobileNumber: "",
      isManualPatient: true,
    });
  };

  // Handle payment method change
  const handlePaymentMethodChange = (selectedOption) => {
    setSelectedPaymentMethod(selectedOption);
    setFormData({ ...formData, paymentMethod: selectedOption?.value || "" });
  };

  // Handle blood group change
  const handleBloodGroupChange = (selectedOption) => {
    setSelectedBloodGroup(selectedOption);
    setFormData({ ...formData, bloodGroup: selectedOption?.value || null });
  };

  // Generic change handler for other fields
  const handleChangeFields = (e) => {
    const { name, value } = e.target;
    if (name === "mobileNumber") {
      let cleanedValue = value.trim();
      if (cleanedValue.startsWith("88")) {
        cleanedValue = cleanedValue.substring(2);
      }
      setFormData({ ...formData, [name]: cleanedValue });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

 useEffect(() => {
        if (!loggedInDoctorDetails?.id || !selectedDate || !Array.isArray(appointmentData)) return;

        const selectedDateString = selectedDate.toLocaleDateString('en-CA');

        const matchingAppointments = appointmentData.filter(app => {
            if (!app.appointmentDate) return false;
            try {
                 const appDateString = new Date(app.appointmentDate).toLocaleDateString('en-CA');
                 return app.doctorId === loggedInDoctorDetails.id && appDateString === selectedDateString;
            } catch (e) {
                console.warn("Invalid appointmentDate found:", app.appointmentDate);
                return false;
            }
        });

        const newSerial = matchingAppointments.length + 1;
        setFormData(prev => ({ ...prev, serialNumber: newSerial }));

    }, [loggedInDoctorDetails, selectedDate, appointmentData]);

  // Reset form function
  const resetForm = () => {
    setFormData(initialFormData);
    setSelectedDoctor(null);
    setSelectedPatient(null);
    setSelectedPaymentMethod({ value: "CASH", label: "Cash" });
    setSelectedFee(null);
    setSelectedBloodGroup(null);
    setSelectedDate(new Date());
    setAvailableDates([]);
    setFeeOptions([]);
  };

  // Handle form submission
 const handleSubmit = async (e) => {
        e.preventDefault();

        if (!loggedInDoctorDetails) { toast.error("Doctor details not loaded."); return; }
        if (!selectedPatient) { toast.warn("Please select or add a patient."); return; }
        if (!formData.consultationFee) { toast.warn("Please select a fee."); return; }
        if (!formData.mobileNumber.trim() && formData.isManualPatient) {
            toast.warn("Please enter a mobile number for the new patient.");
            return;
         }
         if (formData.mobileNumber.trim() && formData.mobileNumber.trim().length < 11) {
            toast.warn("Mobile number must be 11 digits.");
         }

        setLoading(true);
        const formattedDate = selectedDate.toLocaleDateString('en-CA');

        let cleanedMobileNumber = formData.mobileNumber.trim();
        if (cleanedMobileNumber && !cleanedMobileNumber.startsWith("88")) {
            cleanedMobileNumber = "88" + cleanedMobileNumber;
        }

        const payload = {
            doctorId: loggedInDoctorDetails.id,
            doctorName: loggedInDoctorDetails.name || loggedInDoctorDetails.translations?.en?.name || "Doctor",
            patientId:
                selectedPatient && !selectedPatient.value.startsWith("new-")
                    ? selectedPatient.value
                    : null,
            patientName: selectedPatient ? selectedPatient.label : "",
            appointmentDate: formattedDate,
            consultationFee: parseFloat(formData.consultationFee) || 0,
            paymentMethod: formData.paymentMethod || "CASH",
            serialNumber: parseInt(formData.serialNumber) || null,
            mobileNumber: cleanedMobileNumber || null,
            weight: formData.weight ? parseFloat(formData.weight) : null,
            age: formData.age ? parseInt(formData.age) : null,
            reference: formData.reference || null,
            bloodGroup: formData.bloodGroup || null,
            reason: formData.reason || null,
            address: formData.address || null,
            isNewPatient: formData.isManualPatient,
        };

        try {
            const response = await axios.post(`${BASE_URL}/appointment/add`, payload, {
                headers: { "x-api-key": API_KEY },
            });
            toast.success(response.data.message || "Appointment booked successfully!");
            const updatedAppointmentsRes = await fetchData("/appointment");
            if (updatedAppointmentsRes && updatedAppointmentsRes.appointments) {
                setAppointmentData(updatedAppointmentsRes.appointments);
            } else if (Array.isArray(updatedAppointmentsRes)) {
                setAppointmentData(updatedAppointmentsRes);
            }

             if (formData.isManualPatient) {
                const updatedPatientsRes = await axios.get(`${BASE_URL}/patient`, { headers: { "x-api-key": API_KEY } });
                 const patientData = Array.isArray(updatedPatientsRes.data) ? updatedPatientsRes.data : [];
                 const formattedPatients = patientData.map((patient) => ({
                    value: patient.id,
                    label: `${patient.name} (${patient.phoneNumber || "No Phone"})`,
                    phone: patient.phoneNumber || "",
                }));
                setPatients(formattedPatients);
            }

            resetForm(); // Reset form fields
        } catch (error) {
            console.error("Error booking appointment:", error.response?.data || error.message);
            toast.error(error.response?.data?.message || "Failed to book appointment.");
        } finally {
            setLoading(false);
        }
    };
  const breadcrumbs = [
    { label: "Dashboard", url: "/" },
    { label: "My Practise"},
    { label: "Add New Appointment" },
  ];

  // Update available dates for the calendar
  useEffect(() => {
    if (!selectedDoctor) return;
    const doctor = doctors.find((doc) => doc.id === selectedDoctor.value);
    if (!doctor) return;
    const daysMapping = {
      Sunday: 0,
      Monday: 1,
      Tuesday: 2,
      Wednesday: 3,
      Thursday: 4,
      Friday: 5,
      Saturday: 6,
    };
    const availableDays = doctor?.schedule?.map((sch) => daysMapping[sch.day]) || [];
    const calendarStartDate = new Date(selectedDate);
    calendarStartDate.setDate(1);
    const dayOfWeek = calendarStartDate.getDay();
    calendarStartDate.setDate(calendarStartDate.getDate() - dayOfWeek);
    const fullVisibleDates = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(calendarStartDate);
      d.setDate(calendarStartDate.getDate() + i);
      if (availableDays.includes(d.getDay())) {
        fullVisibleDates.push(new Date(d));
      }
    }
    setAvailableDates(fullVisibleDates);
  }, [selectedDoctor, selectedDate, doctors]);

  return (
    <div>
      <ToastContainer />
      <PageHeading title="Add New Appointment" breadcrumbs={breadcrumbs} />
      <div className="">
        <form onSubmit={handleSubmit}>
          <div className="bg-white shadow-sm rounded-lg">
            <div className="flex items-center justify-between border-b border-dashed border-M-text-color/50 p-4">
              <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
                Basic Information
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-5">
              <div>
                <label className="label">Doctor</label>
                <input
                    type="text"
                    className="input-field bg-gray-100 cursor-not-allowed"
                    value={loggedInDoctorDetails?.name || (loggedInDoctorDetails?.translations?.en?.name ?? (doctorLoading || scheduleLoading ? "Loading..." : "N/A"))}
                    readOnly
                />
                </div>

              {/* Patient Selection */}
              <div>
                <label className="label">Select Patient</label>
                {patientLoading ? (
                  <Skeleton height={40} />
                ) : (
                  <CreatableSelect
                    options={patients}
                    value={selectedPatient}
                    onChange={handlePatientChange}
                    onCreateOption={handleCreatePatient}
                    placeholder="Type a name and press Enter to add new patient"
                    isClearable
                    isSearchable
                    formatCreateLabel={(inputValue) => `Add "${inputValue}" as new patient`}
                    styles={{
                      control: (provided) => ({
                        ...provided,
                        borderColor: '#e2e8f0',
                        fontSize: '14px',
                        backgroundColor: '#f7fafc',
                      }),
                      option: (provided, state) => ({
                        ...provided,
                        fontSize: '14px',
                        backgroundColor: state.isSelected
                          ? '#3b82f6'
                          : state.isFocused
                          ? '#ebf8ff'
                          : 'transparent',
                        color: state.isSelected ? '#ffffff' : '#2d3748',
                      }),
                      menu: (provided) => ({
                        ...provided,
                        backgroundColor: '#ffffff',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                        padding: '0px',
                      }),
                    }}
                  />
                )}
              </div>

              {/* Payment Method */}
              <div>
                <label className="label">Payment Method</label>
                <DynamicSelect
                  options={[
                    { value: "CASH", label: "Cash" },
                    { value: "BKASH", label: "Bkash" },
                    { value: "NAGAD", label: "Nagad" },
                    { value: "ROCKET", label: "Rocket" },
                    { value: "BANK", label: "Bank" },
                  ]}
                  value={selectedPaymentMethod}
                  onChange={handlePaymentMethodChange}
                  placeholder="Choose Payment Method"
                  isClearable
                />
              </div>

              {/* Fee Selection */}
              <div>
                <label className="label">Fee</label>
                <DynamicSelect
                  options={feeOptions}
                  value={selectedFee}
                  onChange={handleFeeChange}
                  placeholder="Select Fee"
                  isClearable
                />
              </div>

              {/* Serial Number - Auto-generated */}
              <div>
                <label className="label">Serial Number</label>
                <input
                  type="number"
                  name="serialNumber"
                  className="input-field"
                  value={formData.serialNumber || ""}
                  onChange={handleChangeFields}
                  placeholder="Auto-generated Serial Number"
                  readOnly
                />
              </div>

              {/* Mobile Number */}
              <div>
                <label className="label">Mobile Number</label>
                <input
                  type="number"
                  name="mobileNumber"
                  className="input-field"
                  value={formData.mobileNumber}
                  onChange={handleChangeFields}
                  disabled={formData.isManualPatient ? false : !!selectedPatient}
                />
              </div>

              {/* Blood Group */}
              <div>
                <label className="label">Blood Group</label>
                <DynamicSelect
                  options={[
                    { value: "A_POSITIVE", label: "A+" },
                    { value: "A_NEGATIVE", label: "A-" },
                    { value: "B_POSITIVE", label: "B+" },
                    { value: "B_NEGATIVE", label: "B-" },
                    { value: "O_POSITIVE", label: "O+" },
                    { value: "O_NEGATIVE", label: "O-" },
                    { value: "AB_POSITIVE", label: "AB+" },
                    { value: "AB_NEGATIVE", label: "AB-" },
                  ]}
                  value={selectedBloodGroup}
                  onChange={handleBloodGroupChange}
                  placeholder="Select Blood Group"
                  isClearable
                />
              </div>

              {/* Weight Field */}
              <div>
                <label className="label">Weight (kg)</label>
                <input
                  type="number"
                  name="weight"
                  className="input-field"
                  value={formData.weight}
                  onChange={handleChangeFields}
                  placeholder="Enter weight in kg"
                />
              </div>

              {/* Age Field */}
              <div>
                <label className="label">Age (years)</label>
                <input
                  type="number"
                  name="age"
                  className="input-field"
                  value={formData.age}
                  onChange={handleChangeFields}
                  placeholder="Enter age in years"
                />
              </div>

              {/* Reference Field */}
              <div>
                <label className="label">Reference</label>
                <input
                  type="text"
                  name="reference"
                  className="input-field"
                  value={formData.reference}
                  onChange={handleChangeFields}
                />
              </div>

              {/* Reason */}
              <div>
                <label className="label">Reason</label>
                <textarea
                  name="reason"
                  className="input-field"
                  value={formData.reason || ""}
                  onChange={handleChangeFields}
                  onKeyDown={(e) => {
                    if (e.key === " ") e.stopPropagation();
                  }}
                  placeholder="Enter the reason for the appointment"
                />
              </div>

              {/* Address */}
              <div>
                <label className="label">Address</label>
                <input
                  type="text"
                  name="address"
                  className="input-field"
                  value={formData.address}
                  onChange={handleChangeFields}
                />
              </div>

              {/* Calendar for Appointment solución */}
              <div className="calender">
                {scheduleLoading ? (
                    <Skeleton height={280} width={350} />
                ) : loggedInDoctorDetails ? (
                <Calendar
                    onChange={handleDateChange}
                    onActiveStartDateChange={handleActiveStartDateChange}
                    value={selectedDate}
                    minDate={new Date()}
                    tileDisabled={({ date, view }) => {
                        if (view !== 'month') return false;
                        if (isNaN(date.getTime())) return true;
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        if (date < today) return true;

                        const daysMapping = { Sunday: 0, Monday: 1, Tuesday: 2, Wednesday: 3, Thursday: 4, Friday: 5, Saturday: 6 };
                        const availableDayNumbers = loggedInDoctorDetails.schedule?.map(sch => daysMapping[sch.day]) || [];
                        return !availableDayNumbers.includes(date.getDay());
                        }}
                        tileClassName={({ date, view }) => {
                            if (view !== 'month' || !loggedInDoctorDetails?.schedule) return null;
                            const daysMapping = { Sunday: 0, Monday: 1, Tuesday: 2, Wednesday: 3, Thursday: 4, Friday: 5, Saturday: 6 };
                            const availableDayNumbers = loggedInDoctorDetails.schedule.map(sch => daysMapping[sch.day]);
                            const today = new Date();
                            today.setHours(0,0,0,0);
                            if (availableDayNumbers.includes(date.getDay()) && date >= today) {
                            if (date.toDateString() === today.toDateString()) {
                                    return 'react-calendar__tile--available react-calendar__tile--today-available';
                            }
                            return 'react-calendar__tile--available';
                            }
                            return null;
                        }}
                />
                ) : (
                    <div className="text-center text-gray-500 p-4 border rounded min-h-[280px] flex items-center justify-center">
                        Could not load doctor's schedule.
                    </div>
                )}
            </div>
            </div>
          </div>
          {/* Submit & Reset Buttons */}
          <div className="mt-6 flex gap-4">
            <button
              type="submit"
              className="bg-M-primary-color/90 text-sm text-white py-2 px-6 rounded-md hover:bg-M-primary-color transition-all duration-200"
              disabled={loading || scheduleLoading || !loggedInDoctorDetails}
            >
              {loading ? "Submitting..." : "Submit"}
            </button>
            <button
              type="reset"
              className="bg-[#E7633D]/90 text-sm text-white py-2 px-6 rounded-md hover:bg-[#E7633D] transition-all duration-300"
              onClick={resetForm}
            >
              Clear
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BookAppointment;